import React from 'react';

import shipSvg from '@assets/img/icons/ship.svg';
import truckSvg from '@assets/img/icons/truck.svg';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { getCityLabelByName } from '@data/cities';
import { vesselScheduleStrings } from '@data/vesselSchedule';
import { BookingInquiryRoutePlan } from '@models/next/BookingInquiry';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { formatDate } from '@utils/types';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    port: '港',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  routePlanLineDisplayContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto 1fr auto 1fr auto',
    '&$hasPickups': {
      gridTemplateColumns: 'auto 1fr auto 1fr auto 1fr auto 1fr auto',
    },
  },
  routePlanIconContainer: {
    ...common.flexInline,
    ...common.justifyCenter,
    position: 'relative',
    top: 7,
  },
  routePlanCityLabel: {
    ...common.textGray900,
    ...common.smallerText,
    ...common.flexInline,
    ...common.justifyCenter,
    ...common.whitespacePre,
    paddingBottom: 3,
    width: 30,
    '$disabled &': {
      ...common.textGray700,
    },
  },
  routePlanEnd: {
    ...common.middleText,
    ...common.textGray900,
    ...common.fontWeight600,
    maxWidth: 100,
    '$disabled &': {
      ...common.textGray700,
    },
  },
  routePlanLineContainer: {
    ...common.flexColumn,
    ...common.justifyCenter,
  },
  routePlanLine: {
    borderBottom: `1px dashed ${theme.palette.grey[300]}`,
  },
  routePlanDotContainer: {
    ...common.flexCenter,
  },
  routePlanDot: {
    backgroundColor: theme.palette.grey[400],
    width: 8,
    height: 8,
    borderRadius: 999,
  },
  hasPickups: {},
  routePlanDateCenter: {
    ...common.textPrimary,
    ...common.smallerText,
    ...common.fontWeight600,
    ...common.flexCenter,
    textAlign: 'center',
    whiteSpace: 'pre',
    justifySelf: 'center',
    width: 0,
    '$disabled &': {
      ...common.textGray700,
    },
  },
  disabled: {},
}));

export const RoutePlanLineDisplay: React.FC<{
  routePlan: BookingInquiryRoutePlan;
  showDates?: false | 'all' | 'start-end';
  hideIcons?: boolean;
  disabled?: boolean;
}> = ({ routePlan, showDates, hideIcons = false, disabled = false }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const hasPickups = routePlan.has_pickups;

  const renderLine = () => {
    return (
      <div className={classes.routePlanLineContainer} style={{ padding: 5 }}>
        <div className={classes.routePlanLine} />
      </div>
    );
  };

  const renderDot = () => {
    return (
      <div className={classes.routePlanDotContainer}>
        <div className={classes.routePlanDot} />
      </div>
    );
  };

  const deliveryDate = routePlan.delivery_date;

  const receivingWarehouse = getCityLabelByName(routePlan.receiving_city, '')
    ? `${getCityLabelByName(routePlan.receiving_city)}倉庫`
    : bookingInquiryStrings.warehouse;

  const truckSvgImg = (
    <img
      src={truckSvg}
      alt="Truck"
      style={{ width: 33, height: 19, position: 'relative', top: -3 }}
    />
  );
  const shipSvgImg = (
    <img
      src={shipSvg}
      alt="Ship"
      style={{ width: 33, height: 23, position: 'relative', top: -6 }}
    />
  );

  return (
    <div
      className={clsx(
        classes.routePlanLineDisplayContainer,
        hasPickups && classes.hasPickups,
        disabled && classes.disabled,
      )}
    >
      <div></div>

      {hasPickups && (
        <>
          <div className={classes.routePlanIconContainer}>{!hideIcons && truckSvgImg}</div>
          <div className={classes.routePlanCityLabel}>{receivingWarehouse}</div>
        </>
      )}

      <div className={classes.routePlanIconContainer}>{!hideIcons && truckSvgImg}</div>

      <div className={classes.routePlanCityLabel}>
        {getCityLabelByName(routePlan.pol)}
        {strings.port}
      </div>

      <div className={classes.routePlanIconContainer}>{!hideIcons && shipSvgImg}</div>

      <div className={classes.routePlanCityLabel}>
        {getCityLabelByName(routePlan.pod)}
        {strings.port}
      </div>

      <div className={classes.routePlanIconContainer}>{!hideIcons && truckSvgImg}</div>

      <div></div>

      <div className={classes.routePlanEnd} style={{ padding: 10 }}>
        {hasPickups ? bookingInquiryStrings.pickupAddress : receivingWarehouse}
      </div>

      {hasPickups && (
        <>
          {renderLine()}
          {renderDot()}
        </>
      )}

      {renderLine()}
      {renderDot()}

      {renderLine()}
      {renderDot()}

      {renderLine()}

      <div className={classes.routePlanEnd} style={{ padding: 10 }}>
        {routePlan.receiver_city || bookingInquiryStrings.receiver}
      </div>

      {(showDates === 'all' || showDates === 'start-end') && (
        <>
          <div className={classes.routePlanDateCenter}>
            {formatDate(hasPickups ? routePlan.pickup_date : routePlan.move_in_date)}
            <br />
            {'('}
            {hasPickups ? bookingInquiryStrings.pickupDate : bookingInquiryStrings.moveInDate}
            {')'}
          </div>

          {hasPickups && (
            <>
              <div />
              <div className={clsx(classes.routePlanDateCenter)}>
                {showDates === 'all' && (
                  <>
                    {formatDate(routePlan.warehouse_date)}
                    <br />
                    {'('}
                    {bookingInquiryStrings.warehouseDate}
                    {')'}
                  </>
                )}
              </div>
            </>
          )}

          <div />
          <div className={clsx(classes.routePlanDateCenter)}>
            {showDates === 'all' && (
              <>
                {formatDate(routePlan.pol_date)} <br />
                {'('}
                {vesselScheduleStrings.pol}
                {')'}
              </>
            )}
          </div>

          <div />
          <div className={clsx(classes.routePlanDateCenter)}>
            {showDates === 'all' && (
              <>
                {formatDate(routePlan.pod_date)} <br />
                {'('}
                {vesselScheduleStrings.pod}
                {')'}
              </>
            )}
          </div>

          <div />
          <div className={clsx(classes.routePlanDateCenter)}>
            {formatDate(deliveryDate)}
            <br />
            {'('}
            {bookingInquiryStrings.bestDeliveryDate}
            {')'}
          </div>
        </>
      )}
    </div>
  );
};
